import PropTypes from 'prop-types'
import React, {useState} from 'react'
import actions from 'redux-auto'
import i18n from 'i18next'
import Button from '#components/a/Button'
import Text from '#components/a/Text'
import ModalByProps from '#components/m/ModalByProps'
import Tooltip from '#components/m/Tooltip'
import Headline from '#components/a/Headline'
import UserSearchField from '#components/m/Chat/UserSearchField'
import UserSearchResultSuccess from '#components/m/Chat/UserSearchResultSuccess'
import UserSearchResultError from '#components/m/Chat/UserSearchResultError'

const userIdLenght = 7

function UserSearchModal({triggerConfig, modalConfig}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchResult, setSearchResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const resetSearch = () => {
    setSearchResult(null)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSearchInput('')
    resetSearch()
  }

  const handleConfirm = async () => {
    setIsLoading(true)

    try {
      if (!searchResult) {
        const result = await modalConfig.searchCallback(searchInput)
        setSearchResult(result)
      } else {
        await modalConfig.confirmCallback(searchInput)
        closeModal()
      }
    } catch (e) {
      closeModal()
      actions.error.writeError({error: e})
    } finally {
      setIsLoading(false)
    }
  }

  const hasWarningOrError = searchResult?.user?.warning != null || searchResult?.error != null
  const isInputValid = searchInput.length === userIdLenght
  const isConfirmButtonDisabled = !isInputValid || isLoading || hasWarningOrError
  const confirmButtonText = !searchResult ? i18n.t('misc_search') : i18n.t('misc_add')

  const checkConfirmDisabledReason = () => {
    if (!isInputValid) {
      return modalConfig.confirmDisabledReasons.userIdNotValid
    }
    if (isLoading) {
      if (searchResult) {
        return modalConfig.confirmDisabledReasons.confirmInProgress
      }
      return modalConfig.confirmDisabledReasons.searchInProgress
    }
    if (searchResult?.error) {
      return searchResult?.error.confirmDisabledReason
    }
    if (searchResult?.user?.warning) {
      return searchResult.user.warning.confirmDisabledReason
    }
    return ''
  }
  const confirmDisabledReason = checkConfirmDisabledReason()
  const triggerButtonVariant = triggerConfig.buttonVariant || 'primary'

  return (
    <>
      <Button
        testid={triggerConfig.testId}
        text={triggerConfig.text}
        variant={triggerButtonVariant}
        event={() => setIsModalOpen(true)}
        disabled={triggerConfig?.disabled}
        disabledReason={triggerConfig?.disabledReason}
      />

      <ModalByProps
        testId={modalConfig.testId}
        modalIsOpen={isModalOpen}
        closeModal={closeModal}
        confirmText={confirmButtonText}
        handleConfirm={handleConfirm}
        confirmButtonDisabled={isConfirmButtonDisabled}
        confirmButtonDisabledReason={confirmDisabledReason}
        callbackIsExecuting={isLoading}
      >
        <div className="userSearchModal">
          <div className="userSearchModal__header">
            <Headline modifier="article" importance={2} text={modalConfig.headlineText} />
            {modalConfig.tooltipText && (
              <Tooltip modifier={['dark', 'modal', 'nopadding']} placement="bottom" triggerAction="hover">
                <Text modifier="extrasmall" text={modalConfig.tooltipText} />
              </Tooltip>
            )}
          </div>

          {!searchResult && (
            <UserSearchField
              searchInput={searchInput}
              handleInputChange={input => setSearchInput(input)}
              placeholder={modalConfig.searchFieldPlaceholder}
            />
          )}
          {searchResult && searchResult.error && (
            <UserSearchResultError errorMessage={searchResult.error.message} resetSearch={resetSearch} />
          )}
          {searchResult && searchResult.user && !searchResult.error && (
            <UserSearchResultSuccess
              title={modalConfig.searchResultTitle}
              userId={searchResult.user.id}
              userInfo={searchResult.user.info}
              warning={searchResult.user.warning?.message}
              resetSearch={resetSearch}
            />
          )}
        </div>
      </ModalByProps>
    </>
  )
}

UserSearchModal.propTypes = {
  triggerConfig: PropTypes.shape({
    testId: PropTypes.string,
    text: PropTypes.string,
    disabled: PropTypes.bool,
    disabledReason: PropTypes.string,
    buttonVariant: PropTypes.string,
  }).isRequired,
  modalConfig: PropTypes.shape({
    testId: PropTypes.string,
    headlineText: PropTypes.string,
    tooltipText: PropTypes.string,
    searchFieldPlaceholder: PropTypes.string,
    searchResultTitle: PropTypes.string,
    searchCallback: PropTypes.func,
    confirmCallback: PropTypes.func,
    confirmDisabledReasons: PropTypes.shape({
      userIdNotValid: PropTypes.string,
      searchInProgress: PropTypes.string,
      confirmInProgress: PropTypes.string,
    }),
  }).isRequired,
}

export default UserSearchModal
